<script setup lang="ts">
import { computed } from 'vue'
import { useMapStore } from '@/stores/mapStore.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import FilterButton from '@/components/Elements/Buttons/FilterButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
/**
 * Stores / models
 */
const mapStore = useMapStore()
const mapSettingsStore = useMapSettingsStore()
const mapCoordinateStore = useMapCoordinateStore()
const gameStore = useGameStore()

const largeFleets = computed(() => {
  return Array.from(mapCoordinateStore.fleets.values())
    .filter(fleet => fleet.playerId !== gameStore.player?.id)
    .filter(fleet => fleet.ships > 50)
})

/**
 * variables
 */
const presets = [
  { key: 'planet', value: zoomFactor.Planet },
  { key: 'solar', mobileKey: 'solarShort', value: zoomFactor.Solar },
  { key: 'local', value: zoomFactor.Local },
  { key: 'sector', value: zoomFactor.Sector },
  { key: 'quadrant', mobileKey: 'quadrantShort', value: zoomFactor.Quadrant },
  { key: 'max', value: zoomFactor.Max }
]

/**
 * Functions
 */
const zoom = (zoom: number) => {
  mapStore.zoomFactor = zoom
}

const openSettings = () => {
  gameStore.modalType = 'mapSettings'
}

const toggleLargeFleets = () => {
  mapSettingsStore.showLargeFleets = !mapSettingsStore.showLargeFleets
}
</script>

<template>
  <div class="pt-2 pointer-events-auto flex justify-center xl:justify-end">
    <div class="inline-flex">
      <div
        v-for="preset in presets"
        :key="preset.value"
        :class="preset.key === 'max' ? 'hidden md:block' : ''"
      >
        <FilterButton
          class="mr-2"
          :active="mapStore.zoomFactor === preset.value"
          @click="zoom(preset.value)"
        >
          <template v-if="preset.mobileKey">
            <span class="md:hidden">{{ t('map.zoom.' + preset.mobileKey) }}</span>
            <span class="hidden md:inline">{{ t('map.zoom.' + preset.key) }}</span>
          </template>
          <template v-else>
            {{ t('map.zoom.' + preset.key) }}
          </template>
        </FilterButton>
      </div>
      <ActionButton
        :for-icon="true"
        class="mr-2 xl:hidden relative"
        @click="toggleLargeFleets"
      >
        <GenericIcon
          type="ship"
          :tooltip="t('map.tooltips.toggleLargeFleets')"
          :class="{ 'opacity-50': !mapSettingsStore.showLargeFleets }"
        />
        <span
          v-if="!mapSettingsStore.showLargeFleets && largeFleets.length > 0"
          class="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold"
        >
          {{ largeFleets.length }}
        </span>
      </ActionButton>
      <ActionButton :for-icon="true" class="mr-2" @click="openSettings">
        <GenericIcon type="cog" :tooltip="t('map.tooltips.mapSettings')" />
      </ActionButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Add any additional styles if needed */
</style>
